import global from '@app/filters/global';
import { AbstractPptComponent, IPptComponentPosition } from '@app/services/export/ppt/classes/AbstractPptComponent';
import { createImageForSlide } from '@app/services/export/ppt/utils/layoutUtils';
import { colors } from '@app/services/export/ppt/variables/constants';
import { copy, copyBold, copySmall } from '@app/services/export/ppt/variables/typography';
import GlobalsStoreModule from '@app/store/modules/GlobalsStoreModule';
import { ISeasonPrices } from '@app/types/data/base/IPriceItem';
import { IColumnHeaderTextMap, IPriceTableColumnHeader } from '@app/types/data/base/IPriceTable';
import IPriceTableRow from '@app/types/data/base/IPriceTableRow';
import PptxGenJS from 'pptxgenjs';
import { getModule as getStoreModule } from 'vuex-module-decorators';

import TableRow = PptxGenJS.TableRow;
import TextProps = PptxGenJS.TextProps;
import TableProps = PptxGenJS.TableProps;

interface IProps {
	rows: IPriceTableRow[];
	rowHeight?: number;
	rowLabelWidth?: number;
}

export class PriceTable extends AbstractPptComponent {
	protected readonly slide: PptxGenJS.Slide;

	protected readonly props: IProps;

	protected readonly headerHeight = 0.55;

	protected readonly imageBorderOffset = 0.03;

	protected rowHeight: number;

	protected rowLabelWidth: number;

	protected imageDimension: number;

	protected readonly i18n = getStoreModule(GlobalsStoreModule).i18n;

	public constructor(slide: PptxGenJS.Slide, props: IProps, position: IPptComponentPosition) {
		super(position);
		this.slide = slide;
		this.props = props;
		this.rowLabelWidth = this.props.rowLabelWidth || 1;

		if (this.props.rowHeight) {
			this.rowHeight = this.props.rowHeight;
		} else if (this.hasAnyImage()) {
			this.rowHeight = 0.55;
		} else {
			this.rowHeight = 0.3;
		}

		this.imageDimension = this.rowHeight - this.imageBorderOffset * 2;
	}

	public create(): void {
		this.createTable();
	}

	protected getHeight(): number {
		return this.headerHeight + this.getValidRows().length * this.rowHeight;
	}

	private createTable(): void {
		const tableRows: TableRow[] = [];

		// header
		const tableHeader: TableRow = [];

		if (this.hasRowLabels()) {
			tableHeader.push({ text: '' });
		}

		if (this.hasAnyImage()) {
			tableHeader.push({ text: '' });
		}

		const columnHeaders = this.getColumnHeaders();

		if (columnHeaders) {
			columnHeaders.forEach((item: IPriceTableColumnHeader) => {
				const textArray: TextProps[] = [{ text: item.label }];

				tableHeader.push({
					text: textArray,
					options: {
						...copySmall,
						color: colors.blue,
						align: 'center',
						valign: 'middle'
					}
				});
			});
			tableRows.push(tableHeader);
		}

		// rows
		this.getValidRows().forEach((item, rowIndex) => {
			const tableRow: TableRow = [];
			const individualsValues = item.individuals ? Object.values(item.individuals) : [];
			const groupsValues = item.groups ? Object.values(item.groups) : [];

			if (this.hasAnyImage()) {
				tableRow.push({ text: '' });
			}

			// images
			if (item.image) {
				const imageX = this.position.x + this.imageBorderOffset;
				const imageY = this.position.y + this.headerHeight + rowIndex * this.rowHeight + this.imageBorderOffset;

				createImageForSlide(
					this.slide,
					{ ...item.image, width: 800, height: 800 },
					{
						x: imageX,
						y: imageY,
						destinationWidth: this.imageDimension,
						destinationHeight: this.imageDimension,
						type: 'cover'
					}
				);
			}

			if (this.hasRowLabels()) {
				const labelText: TextProps[] = [];
				labelText.push({ text: item.label, options: { ...copyBold, breakLine: true } });

				if (item.subline) {
					labelText.push({ text: item.subline, options: { ...copySmall } });
				}

				tableRow.push({
					text: labelText,
					options: {
						color: colors.blue,
						valign: 'middle'
					}
				});
			}

			[...individualsValues, ...groupsValues].forEach((value, colIndex) => {
				const isOdd = colIndex % 2 === 0;
				let text: TextProps[] | string;

				// if value is object then it has seasonal prices
				if (typeof value === 'object' && value !== null) {
					const textArray: TextProps[] = [];
					const seasonKeys = Object.keys(value) as (keyof ISeasonPrices)[];

					seasonKeys.forEach((seasonKey: keyof ISeasonPrices): void => {
						const color = colors[seasonKey];

						textArray.push({
							text: global.formatPrice(value[seasonKey]),
							options: {
								lineSpacing: 10,
								breakLine: true,
								highlight: color
							}
						});
					});
					text = textArray;
				} else {
					text = global.formatPrice(value);
				}

				tableRow.push({
					text,
					options: {
						...copy,
						color: colors.blue,
						align: 'center',
						valign: 'middle',
						fill: {
							color: isOdd ? colors.lightGray : colors.white
						}
					}
				});
			});

			tableRows.push(tableRow);
		});

		const tableOptions: TableProps = {
			x: this.position.x,
			y: this.position.y,
			w: this.position.w,
			margin: [0, 0.08, 0, 0.08],
			rowH: [this.headerHeight, ...Array(this.getValidRows().length).fill(this.rowHeight)],
			border: { pt: 0.5, color: colors.bluePale }
		};

		if (this.hasAnyImage()) {
			tableOptions.colW = (tableOptions.colW as number[]) || [];
			tableOptions.colW.push(this.rowHeight);
		}

		if (this.hasRowLabels()) {
			const imagePlaceHolderSpace = this.hasAnyImage() ? this.rowHeight : 0;

			tableOptions.colW = (tableOptions.colW as number[]) || [];
			tableOptions.colW.push(this.rowLabelWidth);

			// rest of the remaining columns, only image and label are fixed width
			tableOptions.colW.push(
				...Array(this.getNumberOfColumns()).fill(
					(this.position.w - imagePlaceHolderSpace - this.rowLabelWidth) / this.getNumberOfColumns()
				)
			);
		}

		this.slide.addTable(tableRows, tableOptions);
	}

	// filter out empty rows
	private getValidRows(): IPriceTableRow[] {
		return this.props.rows.filter((item) => {
			const individualsValues = item.individuals ? Object.values(item.individuals) : [];
			const groupsValues = item.groups ? Object.values(item.groups) : [];
			return individualsValues.length || groupsValues.length;
		});
	}

	// we suppose the columns of all rows is the same, so we can just get the first one
	private getNumberOfColumns(): number {
		const numIndividuals = Object.keys(this.props.rows[0].individuals || {}).length;
		const numGroups = Object.keys(this.props.rows[0].groups || {}).length;
		return numIndividuals + numGroups;
	}

	private hasAnyImage(): boolean {
		return this.props.rows.some((item) => item.image);
	}

	// TODO:  share this logic, at the moment it is copied more or less from present view component PriceTable.vue
	private getColumnHeaders(): IPriceTableColumnHeader[] | null {
		const filters: Record<string, any> = global as Record<string, any>;

		const keysToMap: string[] = [];
		if (this.props.rows[0].individuals) {
			keysToMap.push(...Object.keys(this.props.rows[0].individuals));
		}

		if (this.props.rows[0].groups) {
			keysToMap.push(...Object.keys(this.props.rows[0].groups).map((key: string): string => `${key}Group`));
		}

		const hasYouth: boolean =
			(!!this.props.rows[0].individuals && !!this.props.rows[0].individuals.youth) ||
			(!!this.props.rows[0].groups && !!this.props.rows[0].groups.youth);

		const labelMap: IColumnHeaderTextMap = {
			senior: `${this.i18n.seniors}\n(${filters.formatI18n(this.i18n.fromAge, { age: this.i18n.seniorsMinAge })})`,
			adult: `${this.i18n.adults}\n(${filters.formatI18n(this.i18n.fromAge, {
				age: hasYouth ? this.i18n.adultsMinAgeWithYouth : this.i18n.adultsMinAge
			})})`,
			adultHighSeason: `${this.i18n.adults}\n(${this.i18n.highSeason})`,
			youth: `${this.i18n.youth}\n(${filters.formatI18n(this.i18n.ageRange, {
				minAge: this.i18n.youthMinAge,
				maxAge: this.i18n.youthMaxAge
			})})`,
			pupil: `${this.i18n.pupils}\n(${filters.formatI18n(this.i18n.upToGrade, {
				grade: this.i18n.pupilsMaxGrade
			})}*)`,
			child: `${this.i18n.children}\n(${filters.formatI18n(this.i18n.ageRange, {
				minAge: this.i18n.childrenMinAge,
				maxAge: this.i18n.childrenMaxAge
			})}*)`,
			adultDiscounted: filters.formatI18n(this.i18n.discountCriteria, {
				...this.i18n,
				discountCriteria: undefined // prevent endless loop
			}),
			childDiscounted: filters.formatI18n(this.i18n.discountCriteriaChild, {
				...this.i18n,
				discountCriteriaChild: undefined // prevent endless loop
			}),
			adultGroup: `${this.i18n.adults} - ${this.i18n.groups}\n(${filters.formatI18n(this.i18n.fromAge, {
				age: this.i18n.adultsMinAge
			})})`,
			childGroup: `${this.i18n.children} - ${this.i18n.groups}\n(${filters.formatI18n(this.i18n.ageRange, {
				minAge: this.i18n.childrenMinAge,
				maxAge: this.i18n.childrenMaxAge
			})}*)`
		};

		return keysToMap.map(
			(item: string): IPriceTableColumnHeader => ({
				label: labelMap[item]
			})
		);
	}

	private hasRowLabels(): boolean {
		return this.props.rows.some((item: IPriceTableRow): boolean => !!item.label);
	}
}
